/**
 * React Query hooks for SendRFP data fetching
 *
 * These hooks provide caching, automatic refetching, and loading states
 * for read operations. They consume the low-level client functions.
 *
 * NOTE: useRfpCarriers and useRfpClientDetails are re-exported from @platform/hooks
 * with transformation applied via select. This ensures cache sharing across
 * broker-portal-fe and coverage-overview while returning transformed data.
 */

import { toMinutes, useApi } from "@platform/api";
import {
  rfpBrokerageQueryKey,
  rfpQueryKeys,
  useRfpCarriers as useRfpCarriersShared,
  useRfpClientDetails as useRfpClientDetailsShared,
} from "@platform/hooks";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

import {
  calculateCarrierDueDate,
  getBrokerPartnerUsers,
  getRfpCoverages,
  getRfpDetails,
  getRfpWorkbenchSend,
  transformBrokerPartnerUsersResponse,
  transformCarriersResponse,
  transformClientDetailsResponse,
  transformCoveragesResponse,
  transformWorkbenchSendResponse,
  updateRfpBrokerage,
  updateRfpClient,
  updateRfpDetails,
} from "./client";
import {
  BrokerUserOption,
  CalculateCarrierDueDateResponse,
  RfpDetailsResponse,
  SendRfpCarrier,
  SendRfpClientDetails,
  SendRfpCoverage,
  SendRfpSubmissionData,
  UpdateRfpBrokerageRequest,
  UpdateRfpClientRequest,
  UpdateRfpDetailsRequest,
} from "./types";

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query keys for React Query cache management
 *
 * NOTE: rfpCarriers and rfpClientDetails use shared keys from @platform/hooks
 * for cache sharing with broker-portal-fe.
 */
export const queryKeys = {
  /** Key for RFP carriers query - shared with @platform/hooks */
  rfpCarriers: rfpQueryKeys.carriers,
  /** Key for SendRFP coverages query */
  sendRfpCoverages: (ecoRfpId: string) =>
    ["send-rfp", ecoRfpId, "coverages"] as const,
  /** Key for RFP client details query - shared with @platform/hooks */
  rfpClientDetails: rfpQueryKeys.clientDetails,
  /** Key for RFP brokerage details query */
  rfpBrokerage: rfpBrokerageQueryKey,
  /** Key for broker partner users query */
  brokerPartnerUsers: (brokerId: number) =>
    ["broker", brokerId, "users"] as const,
  /** Key for RFP workbench send query */
  rfpWorkbenchSend: (rfpId: string) =>
    ["rfp", rfpId, "workbench", "send"] as const,
  /** Key for RFP details query */
  rfpDetails: (rfpId: string) => ["rfp", rfpId, "details"] as const,
  /** Key for the carrier due date auto-calculation */
  calculateCarrierDueDate: (rfpId: string) =>
    ["send-rfp", rfpId, "carrier-due-date"] as const,
};

// ============================================================================
// Carrier Hooks
// ============================================================================

interface UseRfpCarriersOptions {
  rfpId: string;
  enabled?: boolean;
}

/**
 * Hook to fetch RFP carriers for the SendRFP flow
 *
 * Returns carriers grouped by connection status with sales rep email availability.
 * Uses the shared hook from @platform/hooks with transformation applied.
 *
 * @param options.rfpId - The broker portal RFP ID to fetch carriers for
 * @param options.enabled - Whether the query should execute (default: true)
 * @returns Query result with transformed carrier data
 *
 * @example
 * ```tsx
 * function CarrierList({ rfpId }: { rfpId: string }) {
 *   const { data: carriers = [], isLoading } = useRfpCarriers({ rfpId });
 *
 *   if (isLoading) return <Spinner />;
 *   return (
 *     <ul>
 *       {carriers.map(carrier => (
 *         <li key={carrier.id}>{carrier.name}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export const useRfpCarriers = ({
  rfpId,
  enabled = true,
}: UseRfpCarriersOptions): UseQueryResult<SendRfpCarrier[], Error> =>
  useRfpCarriersShared({
    rfpId,
    enabled,
    select: transformCarriersResponse,
  });

// ============================================================================
// Coverage Hooks
// ============================================================================

interface UseSendRfpCoveragesOptions {
  ecoRfpId: string;
  enabled?: boolean;
}

/**
 * Hook to fetch RFP coverages for the SendRFP flow
 *
 * Returns coverages with display-friendly labels for chip selection.
 * Uses the lightweight BTX coverages endpoint.
 *
 * @param options.ecoRfpId - The ECO RFP ID (BTX internal ID) to fetch coverages for
 * @param options.enabled - Whether the query should execute (default: true)
 * @returns Query result with transformed coverage data
 *
 * @example
 * ```tsx
 * function CoverageChips({ ecoRfpId }: { ecoRfpId: string }) {
 *   const { data: coverages = [], isLoading } = useSendRfpCoverages({ ecoRfpId });
 *
 *   if (isLoading) return <Spinner />;
 *   return (
 *     <ChipGroup>
 *       {coverages.map(cov => (
 *         <Chip key={cov.id} label={cov.label} />
 *       ))}
 *     </ChipGroup>
 *   );
 * }
 * ```
 */
export const useSendRfpCoverages = ({
  ecoRfpId,
  enabled = true,
}: UseSendRfpCoveragesOptions): UseQueryResult<SendRfpCoverage[], Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: queryKeys.sendRfpCoverages(ecoRfpId),
    queryFn: async () => {
      const response = await getRfpCoverages(ecoRfpId, axiosInstance);
      return transformCoveragesResponse(response);
    },
    enabled: enabled && !!ecoRfpId,
    staleTime: toMinutes(5),
  });
};

// ============================================================================
// Client Details Hooks
// ============================================================================

interface UseRfpClientDetailsOptions {
  rfpId: string;
  enabled?: boolean;
}

/**
 * Hook to fetch client details for an RFP
 *
 * Returns simplified client details (name, situs state, employee count)
 * for display in the SendRFP UI. Uses the shared hook from @platform/hooks.
 *
 * @param options.rfpId - The broker portal RFP ID
 * @param options.enabled - Whether the query should execute (default: true)
 * @returns Query result with transformed client details
 *
 * @example
 * ```tsx
 * function ClientHeader({ rfpId }: { rfpId: string }) {
 *   const { data: client } = useRfpClientDetails({ rfpId });
 *
 *   return <Typography>{client?.name}</Typography>;
 * }
 * ```
 */
export const useRfpClientDetails = ({
  rfpId,
  enabled = true,
}: UseRfpClientDetailsOptions): UseQueryResult<SendRfpClientDetails, Error> =>
  useRfpClientDetailsShared({
    rfpId,
    enabled,
    select: transformClientDetailsResponse,
  });

// ============================================================================
// Brokerage Hooks
// ============================================================================

interface UseBrokerPartnerUsersOptions {
  brokerId: number | null | undefined;
  enabled?: boolean;
}

export const useBrokerPartnerUsers = ({
  brokerId,
  enabled = true,
}: UseBrokerPartnerUsersOptions): UseQueryResult<BrokerUserOption[], Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey:
      brokerId == null
        ? (["broker", "users", "disabled"] as const)
        : queryKeys.brokerPartnerUsers(brokerId),
    queryFn: async () => {
      const response = await getBrokerPartnerUsers(
        brokerId as number,
        axiosInstance,
      );
      return transformBrokerPartnerUsersResponse(response);
    },
    enabled: enabled && brokerId != null,
    staleTime: toMinutes(5),
  });
};

// ============================================================================
// RFP Submission Hooks
// ============================================================================

interface UseRfpWorkbenchSendOptions {
  rfpId: string;
  enabled?: boolean;
}

/**
 * Hook to fetch RFP submission data (effective date, carrier due date, etc.)
 *
 * Returns submission-relevant data from the workbench/send endpoint.
 *
 * @param options.rfpId - The broker portal RFP ID
 * @param options.enabled - Whether the query should execute (default: true)
 * @returns Query result with submission data
 */
export const useRfpWorkbenchSend = ({
  rfpId,
  enabled = true,
}: UseRfpWorkbenchSendOptions): UseQueryResult<
  SendRfpSubmissionData,
  Error
> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: queryKeys.rfpWorkbenchSend(rfpId),
    queryFn: async () => {
      const response = await getRfpWorkbenchSend(rfpId, axiosInstance);
      return transformWorkbenchSendResponse(response);
    },
    enabled: enabled && !!rfpId,
    gcTime: 0, // Remove from cache immediately on unmount to prevent stale data on next mount
  });
};

interface UseCalculateCarrierDueDateOptions {
  rfpId: string;
  enabled?: boolean;
}

/**
 * Hook to auto-populate the carrier due date once per drawer open
 *
 * @param options.rfpId - The broker portal RFP ID
 * @param options.enabled - Whether the request should execute (default: true)
 * @returns Query result with the carrier due date
 */
export const useCalculateCarrierDueDate = ({
  rfpId,
  enabled = true,
}: UseCalculateCarrierDueDateOptions): UseQueryResult<
  CalculateCarrierDueDateResponse,
  Error
> => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.calculateCarrierDueDate(rfpId),
    queryFn: async () => {
      const response = await calculateCarrierDueDate(rfpId, axiosInstance);
      queryClient.invalidateQueries({ queryKey: queryKeys.rfpDetails(rfpId) });
      return response;
    },
    enabled: enabled && !!rfpId,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

interface UseUpdateRfpClientOptions {
  rfpId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to update RFP client details
 *
 * @param options.rfpId - The broker portal RFP ID
 * @param options.onSuccess - Callback on successful update
 * @param options.onError - Callback on error
 * @returns Mutation result
 */
export const useUpdateRfpClient = ({
  rfpId,
  onSuccess,
  onError,
}: UseUpdateRfpClientOptions): UseMutationResult<
  void,
  Error,
  UpdateRfpClientRequest
> => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateRfpClientRequest) =>
      updateRfpClient(rfpId, data, axiosInstance),
    onSuccess: () => {
      // Invalidate related queries to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfpClientDetails(rfpId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfpWorkbenchSend(rfpId),
      });
      onSuccess?.();
    },
    onError,
  });
};

interface UseUpdateRfpBrokerageOptions {
  rfpId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to update RFP brokerage details
 *
 * @param options.rfpId - The broker portal RFP ID
 * @param options.onSuccess - Callback on successful update
 * @param options.onError - Callback on error
 * @returns Mutation result
 */
export const useUpdateRfpBrokerage = ({
  rfpId,
  onSuccess,
  onError,
}: UseUpdateRfpBrokerageOptions): UseMutationResult<
  void,
  Error,
  UpdateRfpBrokerageRequest
> => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateRfpBrokerageRequest) =>
      updateRfpBrokerage(rfpId, data, axiosInstance),
    onSuccess: () => {
      // Invalidate related queries to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfpBrokerage(rfpId),
      });
      onSuccess?.();
    },
    onError,
  });
};

interface UseUpdateRfpDetailsOptions {
  rfpId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to update RFP review details (carrier due date, analysts, etc.)
 *
 * @param options.rfpId - The broker portal RFP ID
 * @param options.onSuccess - Callback on successful update
 * @param options.onError - Callback on error
 * @returns Mutation result
 */
export const useUpdateRfpDetails = ({
  rfpId,
  onSuccess,
  onError,
}: UseUpdateRfpDetailsOptions): UseMutationResult<
  void,
  Error,
  UpdateRfpDetailsRequest
> => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateRfpDetailsRequest) =>
      updateRfpDetails(rfpId, data, axiosInstance),
    onSuccess: () => {
      // Invalidate related queries to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfpDetails(rfpId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfpWorkbenchSend(rfpId),
      });
      onSuccess?.();
    },
    onError,
  });
};

// ============================================================================
// RFP Details Hooks
// ============================================================================

interface UseRfpDetailsOptions {
  rfpId: string;
  enabled?: boolean;
}

/**
 * Hook to fetch the full RFP details
 *
 * @param options.rfpId - The broker portal RFP ID
 * @param options.enabled - Whether the query should execute (default: true)
 * @returns Query result with full RFP details
 */
export const useRfpDetails = ({
  rfpId,
  enabled = true,
}: UseRfpDetailsOptions): UseQueryResult<RfpDetailsResponse, Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: queryKeys.rfpDetails(rfpId),
    queryFn: () => getRfpDetails(rfpId, axiosInstance),
    enabled: enabled && !!rfpId,
    staleTime: toMinutes(5),
  });
};
